// ════════════════════════════════════════════════════════════════════════════
//  ManDana — a line that is only a link
//  ---------------------------------------------------------------------------
//  ONE RULE, said once and obeyed everywhere: a line of writing that is nothing
//  but a link is drawn as the thing it points at. A video plays there, a picture
//  is shown there, a document is opened there, and any other address becomes a
//  card carrying whatever we can honestly say about it.
//
//  Everything else stays what it was. A link inside a sentence is a link inside
//  a sentence — md-link.ts renders it and this module never sees it — because a
//  paragraph that mentions a video is writing ABOUT a video, and replacing the
//  person's words with a player takes their own writing off the screen.
//
//  ── WHY THIS IS THE TEXT AND NOT A FIELD ──────────────────────────────────
//  The embed IS the writing. That is what makes the caret work: you can stand
//  above it, below it, put two in one record, drag one between paragraphs, or
//  delete it by deleting the line — none of which is available to something
//  drawn beside the text out of a field the writer cannot see.
//
//  It also means an embed rides the record it belongs to through sync, through
//  a board, through export, with no second document to keep in step. md-record.ts
//  calls this one species; this is what one species buys.
//
//  ── THE THREE FORMS, AND WHY THE WRITER NEEDS ALL THREE ───────────────────
//      https://youtu.be/abc                     a bare link, alone on its line
//      ![Some name](https://…/p.png =620x380)   markdown's own "show this"
//      [Some name](https://youtu.be/abc)        markdown's own "name this"
//
//  The bare form is the default and it is a GUESS: the address is asked what it
//  is, and a video link is a video, an address ending .png is a picture, and
//  anything else is a card naming where it goes. The guess is right most of the
//  time and wrong in two ways the writer can see and we cannot:
//
//    • most pictures on the web do not end in `.png`. A CDN serves a photograph
//      from `/photo-1682?auto=format&w=1200`, and no amount of parsing gets a
//      picture out of that. `![…](…)` is the writer saying so — markdown's own
//      "show this", where what the address admits to stops mattering;
//    • sometimes a video is a REFERENCE rather than something to watch here.
//      `[…](…)` is markdown's own "name this", and it draws the card.
//
//  So the format is the writer's, in the writing, in markdown — not a property
//  hidden in a menu. Nothing is invented: both bracket forms are standard, and
//  the optional size is the `=WxH` spelling markdown-it has used for years.
//  What is written is legible to anybody reading the raw text, and what any
//  other markdown reader makes of it is close enough to right.
//
//  A DOCUMENT ON THIS DEVICE is written the same way, with an `mdfile:` target:
//      ![Al-Ghazali.pdf](mdfile:9fx2k3j1)
//  The id is a content hash and means nothing on a device that does not hold the
//  bytes — which is precisely why the NAME is in the line beside it. Every
//  device can say what the document is; the one holding it can also show it.
//  (md-file.ts used to carry this in a record field, for fear of writing an
//  unreadable token into somebody's prose. The token is never rendered as text —
//  it renders as the document — so what the fear protected against cannot
//  happen, and being in the writing is what lets the caret past it.)
//
//  ── AND THE ONE FORM THAT IS DELIBERATELY NOT A CARD ──────────────────────
//      <https://example.com>                    markdown's autolink
//  A writer who wants the address itself on a line, and not a card, needs a way
//  to say so — this is markdown's own way, and md-markdown.jsx dims the angle
//  brackets the way it dims every other marker.
// ════════════════════════════════════════════════════════════════════════════

/** What one line of writing turned out to be. `null` from mdEmbedLine means the
    line is ordinary text and nothing here applies to it. */
interface MdEmbed {
  /** How it should be drawn. 'link' is the honest fallback: an address we can
      show a card for but cannot play, render or open in place. */
  kind: 'video' | 'image' | 'file' | 'link';
  /** Which of the three spellings this line is written in. Carried because a
      line has to be rewritten at its own size without changing what it means —
      see mdEmbedWrite. */
  form: 'bare' | 'show' | 'card';
  /** Identity. Two lines with the same key are the same embed, which is what
      keeps a playing video from being torn down and rebuilt as the writing
      around it changes. The size is NOT part of it — resizing must not restart
      a video. */
  key: string;
  /** What the writer called it, or '' when they never said. */
  label: string;
  /** The target exactly as written. */
  target: string;
  /** The safe, resolved address — '' for a document, which has no address. */
  href: string;
  /** The file id for `kind: 'file'`, else ''. */
  file: string;
  /** The parsed media for a video or a picture, else null. */
  media: unknown;
  /** The size the writer dragged it to, in CSS pixels; 0 for "as it comes". */
  w: number;
  h: number;
}

(function (g: any) {
  'use strict';

  /* Documents live on the device, not on the web, so they need a scheme of our
     own. It is deliberately not one a browser will follow: mdLinkHref refuses
     it like any other unknown scheme, so a document link can never be turned
     into a navigation by any path in the app. */
  const MD_FILE_SCHEME = 'mdfile:';
  const FILE_ID = /^[a-z0-9]{6,64}$/;

  /* `![label](target =WxH)` and `[label](target =WxH)` — the label and the size
     both optional in each. The target stops at whitespace or `)`, so a size can
     be told from an address without a lookahead, and a target containing a
     space is refused rather than silently cut in half. */
  const IMG_LINE = /^!\[([^\]\n]*)\]\(\s*([^\s)]+)(?:\s+=(\d{1,5})x(\d{1,5}))?\s*\)$/;
  const CARD_LINE = /^\[([^\]\n]*)\]\(\s*([^\s)]+)(?:\s+=(\d{1,5})x(\d{1,5}))?\s*\)$/;

  /* A size a person could plausibly have dragged to. Anything outside it is a
     line that has been corrupted or hand-edited into nonsense, and the answer to
     that is the default size rather than a card the height of a building. */
  const MD_EMBED_MIN = 80;
  const MD_EMBED_MAX = 4000;

  function sizeOk(n: number): boolean {
    return Number.isFinite(n) && n >= MD_EMBED_MIN && n <= MD_EMBED_MAX;
  }

  /** The id in an `mdfile:` target, or '' when it is not one (or not a real id). */
  function mdEmbedFileId(target: unknown): string {
    if (typeof target !== 'string') return '';
    if (target.slice(0, MD_FILE_SCHEME.length) !== MD_FILE_SCHEME) return '';
    const id = target.slice(MD_FILE_SCHEME.length).trim().toLowerCase();
    return FILE_ID.test(id) ? id : '';
  }

  /** The line a document is written as. The name comes first because that is
      what every device can read; the id is the part only one of them can use. */
  function mdEmbedFileLine(ref: unknown): string {
    const r = ref as { id?: string; name?: string } | null;
    if (!r || typeof r !== 'object' || !r.id) return '';
    /* A name with a `]` in it would close the label early and turn the rest into
       prose, so the two characters that can break the form are the two that get
       taken out. Everything else a filename can contain is left alone. */
    const label = String(r.name || 'Document').replace(/[\[\]]/g, ' ').trim();
    return `![${label}](${MD_FILE_SCHEME}${r.id})`;
  }

  function build(kind: string, form: string, target: string, label: string, href: string,
    file: string, media: unknown, w: number, h: number): MdEmbed {
    return { kind, form, key: target, label, target, href, file, media, w, h } as MdEmbed;
  }

  /** What this line is, or null for ordinary writing. */
  function mdEmbedLine(line: unknown): MdEmbed | null {
    if (typeof line !== 'string') return null;
    const s = line.trim();
    if (!s || s.length > 2000) return null;

    let target = s, label = '', w = 0, h = 0, form = 'bare';
    let m = IMG_LINE.exec(s);
    if (m) form = 'show';
    else if ((m = CARD_LINE.exec(s))) form = 'card';
    if (m) {
      label = String(m[1] || '').trim();
      target = String(m[2] || '');
      const pw = m[3] ? parseInt(m[3], 10) : 0;
      const ph = m[4] ? parseInt(m[4], 10) : 0;
      if (sizeOk(pw) && sizeOk(ph)) { w = pw; h = ph; }
    } else {
      /* A bare line. A space anywhere in it makes it prose — see md-media.ts on
         why that restraint matters — and the angle-bracket form is a writer
         saying "leave this as an address", so it is refused here by falling
         through to mdLinkHref, which never resolves `<…>` as written. */
      if (s.indexOf(' ') >= 0) return null;
    }

    const file = mdEmbedFileId(target);
    if (file) return build('file', form, target, label, '', file, null, w, h);

    const href = g.mdLinkHref ? g.mdLinkHref(target) : null;
    if (!href) return null;

    /* "Name this" is asked before anything is parsed, and that order IS the
       feature: a writer who wrote the card form gets a card even for a video we
       could perfectly well play. Nothing is framed for this line — `media` is
       null — so the choice is not merely cosmetic. */
    if (form === 'card') return build('link', form, target, label, href, '', null, w, h);

    const media = g.mdMediaParse ? g.mdMediaParse(target) : null;
    if (media) return build(media.kind, form, target, label, href, '', media, w, h);

    /* "Show this", on an address that admits to nothing. The writer has told us
       what it is and there is no reason to disbelieve them: an <img> cannot run
       what it is handed, and when the address turns out not to be a picture
       after all, md-embed.jsx says so in the card's own quiet slot. */
    if (form === 'show') {
      const pic = g.mdMediaImage ? g.mdMediaImage(target) : null;
      if (pic) return build('image', form, target, label, href, '', pic, w, h);
    }

    /* Everything else that is genuinely a web address. It cannot be framed —
       most sites refuse to be, and our own Content-Security-Policy names the two
       hosts that may be (see public/_headers) — so it is drawn as a card that
       says where it goes. */
    return build('link', form, target, label, href, '', null, w, h);
  }

  /** Every embed in a body, with the line each one stands on. */
  function mdEmbedScan(text: unknown): Array<MdEmbed & { line: number }> {
    const out: Array<MdEmbed & { line: number }> = [];
    if (typeof text !== 'string' || !text) return out;
    const lines = text.split('\n');
    for (let i = 0; i < lines.length; i++) {
      const found = mdEmbedLine(lines[i]);
      if (found) out.push(Object.assign({ line: i }, found));
    }
    return out;
  }

  /** Whether a BARE line of this target already means what `form` asks for. It
      is the whole of the "shortest spelling" rule below: brackets exist to say
      something the address does not, and on a video link `![…](…)` says nothing
      at all. */
  function meansSame(target: string, form: string): boolean {
    if (form === 'bare') return true;
    const media = g.mdMediaParse ? g.mdMediaParse(target) : null;
    return form === 'show' ? !!media : !media;
  }

  /** The line an embed should be written as, in the spelling it is asked for.
      Two rules, and the second is the one that keeps records honest:

      THE SHORTEST SPELLING THAT STILL MEANS THIS. A person pasting a video link
      types the address and nothing else; rewriting it into `![](…)` adds
      punctuation to their writing and says nothing the address did not.

      AND A SIZE NEVER CHANGES A MEANING. `form: 'bare'` has no brackets to put
      a size in, so writing one has to promote the line — and the bracket it
      promotes to is whichever one preserves what the line already drew as. Get
      this wrong and dragging the corner of a bookmark turns it into a picture
      of a web page, which is the kind of defect a record keeps forever. */
  function mdEmbedWrite(target: unknown, label?: unknown, w?: unknown, h?: unknown,
    form?: unknown): string {
    const t = typeof target === 'string' ? target.trim() : '';
    if (!t || t.indexOf(' ') >= 0 || t.indexOf('\n') >= 0) return '';
    const name = typeof label === 'string' ? label.replace(/[\[\]]/g, ' ').trim() : '';
    const pw = typeof w === 'number' ? Math.round(w) : 0;
    const ph = typeof h === 'number' ? Math.round(h) : 0;
    const size = sizeOk(pw) && sizeOk(ph) ? ` =${pw}x${ph}` : '';
    const want = form === 'show' || form === 'card' ? String(form) : 'bare';
    if (!name && !size && !mdEmbedFileId(t) && meansSame(t, want)) return t;
    /* A document has no bare spelling — an `mdfile:` id means nothing without
       the name beside it — so it is always shown, never carded. */
    const shown = want !== 'bare' ? want
      : (mdEmbedFileId(t) || (g.mdMediaParse && g.mdMediaParse(t))) ? 'show' : 'card';
    return `${shown === 'card' ? '' : '!'}[${name}](${t}${size})`;
  }

  /** The same embed at a new size — used by the drag handle, which knows the
      pixels and nothing else about the line it is changing. Passing 0 for both
      takes the size back off, which is what "Reset size" means. */
  function mdEmbedResize(line: unknown, w: unknown, h: unknown): string {
    const found = mdEmbedLine(line);
    if (!found) return typeof line === 'string' ? line : '';
    return mdEmbedWrite(found.target, found.label, w, h, found.form);
  }

  /** What this line could honestly be drawn as, in the order a menu should
      offer them. HONESTLY is the whole of it — every row this returns has to be
      a thing that can actually happen when it is picked:

        • a document is absent entirely. Bytes on this device are a document and
          nothing else, and there is no second way to draw one;
        • a video is never offered as a picture. A watch page in an <img> is a
          broken picture with certainty, not a guess that might come off, and an
          option that cannot work is worse than one that is not there. Its
          alternatives are the card and the bare address, which both do work. */
  function mdEmbedKinds(embed: unknown): string[] {
    const e = embed as MdEmbed | null;
    if (!e || typeof e !== 'object' || !e.href) return [];
    const media = g.mdMediaParse ? g.mdMediaParse(e.target) : null;
    if (media && media.kind === 'video') return ['video', 'link', 'plain'];
    return ['image', 'link', 'plain'];
  }

  /** The same line, drawn a different way. The one thing that is deliberately
      NOT carried across is the size: a picture and a card are different shapes,
      and 620×84 dragged for a bookmark is a letterbox to a photograph. The name
      IS carried, because it is the writer's own word for the thing. */
  function mdEmbedFormat(line: unknown, want: unknown): string {
    const e = mdEmbedLine(line);
    if (!e) return typeof line === 'string' ? line : '';
    /* Out of card-land altogether, in markdown's own way of saying "this is an
       address and it stays one" — the same spelling the paste menu writes, so
       there is one way to be a plain link rather than two. */
    if (want === 'plain') return e.href ? `<${e.target}>` : e.target;
    const form = want === 'image' ? 'show' : want === 'link' ? 'card' : 'bare';
    return mdEmbedWrite(e.target, e.label, 0, 0, form);
  }

  /* ── the one piece of history this module carries ──────────────────────────
     Records made before documents lived in the writing keep their `file` field.
     Rather than a compatibility branch in every surface that draws a record,
     the field is folded into the text once, here, wherever a record is read.
     Nothing is rewritten in storage by this: the next save the writer makes
     persists the line and the field simply stops being consulted. */
  function mdEmbedLegacy(text: unknown, file: unknown): string {
    const body = typeof text === 'string' ? text : '';
    const ref = file as { id?: string } | null;
    if (!ref || typeof ref !== 'object' || !ref.id) return body;
    if (body.indexOf(MD_FILE_SCHEME + ref.id) >= 0) return body;
    const line = mdEmbedFileLine(ref);
    if (!line) return body;
    return body ? `${body}\n\n${line}` : line;
  }

  g.MD_FILE_SCHEME = MD_FILE_SCHEME;
  g.MD_EMBED_MIN = MD_EMBED_MIN;
  g.MD_EMBED_MAX = MD_EMBED_MAX;
  g.mdEmbedLine = mdEmbedLine;
  g.mdEmbedScan = mdEmbedScan;
  g.mdEmbedWrite = mdEmbedWrite;
  g.mdEmbedResize = mdEmbedResize;
  g.mdEmbedKinds = mdEmbedKinds;
  g.mdEmbedFormat = mdEmbedFormat;
  g.mdEmbedFileId = mdEmbedFileId;
  g.mdEmbedFileLine = mdEmbedFileLine;
  g.mdEmbedLegacy = mdEmbedLegacy;

  // CommonJS escape hatch, following md-media.ts and md-record.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { MD_FILE_SCHEME, MD_EMBED_MIN, MD_EMBED_MAX, mdEmbedLine,
      mdEmbedScan, mdEmbedWrite, mdEmbedResize, mdEmbedKinds, mdEmbedFormat,
      mdEmbedFileId, mdEmbedFileLine, mdEmbedLegacy };
  }
})(typeof window !== 'undefined' ? window : globalThis);
